/**
 * The Tasks rail — `docs/activity.md` §8.6 (Board 9 A, Board 19 A, B): 232
 * wide on a desktop and an iPad, List and Board alike. *Tasks for* · the
 * people button's people (selected while no list is open) · *Task lists*
 * with **+ New** · a row per list this person can see · and, while no list
 * is open, the note. On a phone the same rows are in the page bar's view
 * button's sheet, under the *List · Board* toggle (`PhoneTasksView`, Aaron,
 * Oct 27 — it was a line of its own above the list, Board 16 C).
 */
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react'
import { firstNameOf, peopleLabel, type ActivityPerson } from '@rowted/shared'
import { BottomSheet } from '../sheet.js'
import { cn } from '../../lib/cn.js'
import { activityRequest, peopleQuery } from '../api.js'
import { useActivity } from '../activity-context.js'
import { Glyph } from '../glyphs.js'
import { Face, UnassignedFace } from '../faces.js'
import { TASKS_CHANGED_EVENT } from './task-sheet.js'
import type { TaskList } from './types.js'

/** The people button's pick, as the rail and the phone line say it. */
export function usePickWords(): { label: string; sub: string; one: ActivityPerson | null; onlyUnassigned: boolean } {
  const { directory, state, people, unassigned } = useActivity()
  const byId = new Map((directory?.people ?? []).map((p) => [p.id, p]))
  const shown = people.map((id) => byId.get(id)).filter((p): p is ActivityPerson => Boolean(p))
  const group = directory?.groups.find((g) => g.id === state.people.groupId) ?? null
  const units = unassigned
    .map((id) => directory?.unassigned.find((u) => u.orgUnitId === id)?.name)
    .filter((name): name is string => Boolean(name))
  const onlyUnassigned = shown.length === 0 && units.length > 0
  const label = onlyUnassigned
    ? peopleLabel({ names: [], unassigned: units })
    : peopleLabel({ names: shown.map((p) => p.name), groupName: group?.name, unassigned: [] })
  const sub = !onlyUnassigned && units.length > 0 ? 'with Unassigned' : 'from the people button'
  return { label, sub, one: shown.length === 1 && units.length === 0 ? shown[0]! : null, onlyUnassigned }
}

/**
 * How many open tasks the people button's pick has — the Board's distinct
 * count (a task shared by two of them counts once), which is *My open*'s
 * number for one person (Board 9 A: 8) and the Board's for a group (19 A: 21).
 */
export function usePickOpenCount(): number | null {
  const { people, unassigned } = useActivity()
  const [count, setCount] = useState<number | null>(null)
  const [tick, setTick] = useState(0)
  useEffect(() => {
    const again = () => setTick((n) => n + 1)
    window.addEventListener(TASKS_CHANGED_EVENT, again)
    return () => window.removeEventListener(TASKS_CHANGED_EVENT, again)
  }, [])
  useEffect(() => {
    let live = true
    activityRequest<{ open: number }>(
      `/activity/task-board?people=${peopleQuery(people)}&unassigned=${peopleQuery(unassigned)}&done=0`)
      .then((answer) => { if (live) setCount(answer.open) })
      .catch(() => { if (live) setCount(null) })
    return () => { live = false }
  }, [people, unassigned, tick])
  return count
}

function Count({ n, on }: { n: number | null; on: boolean }) {
  if (n === null) return null
  return (
    <span className={cn('shrink-0 rounded-full px-[7px] py-px text-[11px] font-semibold text-text-muted', on ? 'bg-surface' : 'bg-line-soft')}>
      {n}
    </span>
  )
}

function Head({ children, right }: { children: ReactNode; right?: ReactNode }) {
  return (
    <div className="mb-1.5 mt-3.5 flex items-center text-[10.5px] font-semibold uppercase tracking-[.06em] text-text-faint">
      {children}<span className="flex-1" />{right}
    </div>
  )
}

function RailRow({
  on, tile, title, sub, count, onClick,
}: { on: boolean; tile: ReactNode; title: string; sub: string; count: number | null; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={on}
      className={cn('-mx-2 flex w-[calc(100%+16px)] items-center gap-[9px] rounded-[9px] px-2 py-[7px] text-left',
        on ? 'bg-accent-soft' : 'hover:bg-hover')}>
      {tile}
      <span className="flex min-w-0 flex-1 flex-col">
        <b className={cn('truncate text-[12.5px] font-semibold', on ? 'text-accent-dark' : 'text-text')}>{title}</b>
        <span className="truncate text-[11px] text-text-muted">{sub}</span>
      </span>
      <Count n={count} on={on} />
    </button>
  )
}

function Tile({ on, glyph }: { on: boolean; glyph: 'people' | 'board' }) {
  return (
    <span className={cn('inline-flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-lg',
      on ? 'bg-surface text-accent' : 'bg-line-soft text-text-muted')}>
      <Glyph name={glyph} size={14} />
    </span>
  )
}

export function PickTile({ on }: { on: boolean }) {
  const pick = usePickWords()
  if (pick.one) {
    return (
      <span className="inline-flex shrink-0 rounded-full border-[1.5px] border-surface">
        <Face person={pick.one} size={22} />
      </span>
    )
  }
  if (pick.onlyUnassigned) return <UnassignedFace size={26} />
  return <Tile on={on} glyph="people" />
}

export function TaskRail({
  lists, listId, onPick, onNew,
}: {
  lists: readonly TaskList[]
  listId: string | null
  onPick: (listId: string | null) => void
  onNew: () => void
}) {
  const pick = usePickWords()
  const open = usePickOpenCount()
  return (
    /* `leading-[normal]`: Board 9's rail sets no line-height of its own. */
    <aside className="w-[232px] shrink-0 self-stretch border-r border-line bg-surface px-4 pb-3.5 pt-1 leading-[normal]">
      <Head>Tasks for</Head>
      <RailRow on={listId === null} tile={<PickTile on={listId === null} />} title={pick.label} sub={pick.sub}
        count={open} onClick={() => onPick(null)} />
      <Head right={(
        <button type="button" onClick={onNew}
          className="inline-flex items-center gap-[3px] text-[12px] normal-case tracking-normal text-accent">
          <Glyph name="plus" size={12} />New
        </button>
      )}>Task lists</Head>
      {lists.map((list) => (
        <RailRow key={list.id} on={list.id === listId} tile={<Tile on={list.id === listId} glyph="board" />}
          title={list.name} sub={list.sub} count={list.open} onClick={() => onPick(list.id)} />
      ))}
      {listId === null && (
        <div className="mt-3 rounded-[10px] bg-surface-alt px-[11px] py-2.5 text-[11.5px] leading-[1.45] text-text-muted">
          A task in a list also shows in the tasks of everyone it’s assigned to.
        </div>
      )}
    </aside>
  )
}

export type TasksView = 'list' | 'board'

const VIEW_WORDS: Record<TasksView, string> = { list: 'List', board: 'Board' }
const VIEW_GLYPHS = { list: 'rows', board: 'board' } as const satisfies Record<TasksView, string>

/**
 * The phone's one control for what Tasks shows (Aaron, Oct 27: "consolidate
 * the list / board changer and the tasks for switcher … List / Board would be
 * a toggle at the top … then the list of task lists and people to view below
 * it") — the page bar's view button, where *List · Board* was, saying what is
 * showing: the view's glyph (*rows* for List, *board* for Board — the sheet's
 * toggle wears the same two) and whose tasks or which list (*Tom*, *Sales
 * meeting*), its name for a screen reader (*Tasks view: Tom · List*). The
 * glyph rather than the word, because on a list's Board the button is the
 * only place the list's name is said, and a 390 px bar has room for one of
 * the two. It takes what the bar has left and no more (`useRoom`), so
 * *Activity* is never the one cut short when the faces button is wide. The
 * bar that said *Tasks for* under the page bar is gone.
 *
 * Its sheet: the **List · Board** toggle at the top, then the rail's own rows
 * (§8.6) — the people button's people and every task list, **+ New**. The
 * toggle changes the view at once and the sheet stays, so a list can be picked
 * under it; a list picked after the toggle was touched opens in the view the
 * toggle shows, else in the one it was last in (each list remembers its own).
 * Picking a row closes the sheet; back, the scrim, × and Escape close it too.
 */
/**
 * How wide the view button may be: the page bar's row (the window less its
 * 16 px sides) less *Activity* and its gap, the faces button, **+** and the
 * two gaps between them — at least room for the glyph and a few letters, and
 * never wider than the other tabs' buttons grow.
 */
function useRoom(button: RefObject<HTMLElement | null>): number {
  const [room, setRoom] = useState(148)
  useLayoutEffect(() => {
    /* The bar this button is in: the nearest box that also holds the title. */
    let bar = button.current?.parentElement ?? null
    while (bar && !bar.querySelector('h1')) bar = bar.parentElement
    const people = bar?.querySelector<HTMLElement>('[data-people-button]') ?? null
    const title = bar?.querySelector<HTMLElement>('h1') ?? null
    const fit = () => {
      /* The widths are rounded, and a title a pixel short of its words is a title with an ellipsis: 2 to spare. */
      const taken = (title?.scrollWidth ?? 68) + 10 + 8 + (people?.offsetWidth ?? 60) + 8 + 44 + 2
      setRoom(Math.max(76, Math.min(160, window.innerWidth - 32 - taken)))
    }
    fit()
    /* Again once the bar's font is in: *Activity* in the fallback face is narrower. */
    let live = true
    void document.fonts?.ready.then(() => { if (live) fit() })
    const watch = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(fit)
    if (people) watch?.observe(people)
    window.addEventListener('resize', fit)
    return () => { live = false; watch?.disconnect(); window.removeEventListener('resize', fit) }
  }, [button])
  return room
}

export function PhoneTasksView({
  lists, listId, view, onView, onPick, onNew,
}: {
  lists: readonly TaskList[]
  listId: string | null
  view: TasksView
  onView: (view: TasksView) => void
  /** A row: that list (`null`, the people's tasks) — in `as` when the toggle was touched first. */
  onPick: (listId: string | null, as?: TasksView) => void
  onNew: () => void
}) {
  const pick = usePickWords()
  const open = usePickOpenCount()
  const [sheet, setSheet] = useState(false)
  const [toggled, setToggled] = useState(false)
  const list = lists.find((l) => l.id === listId) ?? null
  const who = list ? list.name : pick.one ? firstNameOf(pick.one.name) : pick.label
  const viewWords = VIEW_WORDS[view]
  const close = () => { setSheet(false); setToggled(false) }
  const ref = useRef<HTMLButtonElement>(null)
  const room = useRoom(ref)
  return (
    <>
      {/* The other tabs' view button's furniture (`ViewMenu`), so the row reads the same on every tab. */}
      <button ref={ref} type="button" data-view-button data-view={viewWords} aria-haspopup="dialog" aria-expanded={sheet}
        aria-label={`Tasks view: ${who} · ${viewWords}`} onClick={() => setSheet(true)} style={{ maxWidth: room }}
        className="inline-flex h-11 min-w-0 shrink-0 items-center gap-1 rounded-[10px] border border-line bg-surface pl-2.5 pr-2 text-[13.5px] font-semibold text-text hover:bg-hover">
        <Glyph name={VIEW_GLYPHS[view]} size={15} className="shrink-0 text-text-muted" />
        <span className="min-w-0 truncate">{who}</span>
        <Glyph name="chevronDown" size={12} stroke={2.2} className="shrink-0 text-text-muted" />
      </button>
      {sheet && (
        <BottomSheet title="Tasks view" sub="remembered on this phone" label="Tasks view" onClose={close}
          bodyClassName="px-4 pb-4 pt-3">
          {/* The segmented track's tokens at a finger's 44 px (`Segmented`'s own `touch` is 34, and stacks a glyph). */}
          <div role="radiogroup" aria-label="List or Board" className="flex rounded-touch bg-segmented-track p-[3px]">
            {(['list', 'board'] as const).map((value) => (
              <button key={value} type="button" role="radio" aria-checked={view === value}
                onClick={() => { setToggled(true); if (value !== view) onView(value) }}
                className={cn('inline-flex h-11 flex-1 items-center justify-center gap-1.5 rounded-control text-[14px] font-semibold',
                  view === value ? 'bg-surface text-text shadow-segmented' : 'text-text-muted')}>
                <Glyph name={VIEW_GLYPHS[value]} size={15} />{VIEW_WORDS[value]}
              </button>
            ))}
          </div>
          <Head>Tasks for</Head>
          <TaskRailBody lists={lists} listId={listId} openCount={open}
            onPick={(id) => {
              const as = toggled ? view : undefined
              close()
              onPick(id, as)
            }}
            onNew={() => { close(); onNew() }} />
        </BottomSheet>
      )}
    </>
  )
}

/** The rail's rows at 52 px, for the phone's sheet. */
function TaskRailBody({
  lists, listId, openCount, onPick, onNew,
}: {
  lists: readonly TaskList[]; listId: string | null; openCount: number | null
  onPick: (listId: string | null) => void; onNew: () => void
}) {
  const pick = usePickWords()
  const row = (on: boolean, tile: ReactNode, title: string, sub: string, count: number | null, onClick: () => void, key: string) => (
    <button key={key} type="button" onClick={onClick}
      className={cn('flex min-h-[52px] w-full items-center gap-2.5 rounded-[10px] px-2 text-left', on ? 'bg-accent-soft' : '')}>
      {tile}
      <span className="flex min-w-0 flex-1 flex-col">
        <b className={cn('truncate text-[14px] font-semibold', on ? 'text-accent-dark' : 'text-text')}>{title}</b>
        <span className="truncate text-[12px] text-text-muted">{sub}</span>
      </span>
      <Count n={count} on={on} />
    </button>
  )
  return (
    <>
      {row(listId === null, <PickTile on={listId === null} />, pick.label, pick.sub, openCount, () => onPick(null), 'pick')}
      <Head right={(
        <button type="button" onClick={onNew} className="inline-flex items-center gap-[3px] text-[13px] normal-case tracking-normal text-accent">
          <Glyph name="plus" size={13} />New
        </button>
      )}>Task lists</Head>
      {lists.map((list) => row(list.id === listId, <Tile on={list.id === listId} glyph="board" />, list.name, list.sub,
        list.open, () => onPick(list.id), list.id))}
    </>
  )
}

